import { useState } from 'react';
import { Box, Card, Flex, Heading, IconButton, Menu, Portal, Tag } from '@chakra-ui/react';
import { LuEllipsisVertical, LuExternalLink } from 'react-icons/lu';
import { useAuthContext } from '@contexts/auth';
import type { PortfolioType } from '@customTypes/portfolio';
import CreatedBy from '@components/CreatedBy';

import ConfirmDisableDialog from './ConfirmDisableDialog';
import UpsertDialog from './UpsertDialog';

type Props = {
  portfolio: PortfolioType,
  refetch: () => void,
};

const PortfolioCard = ({ portfolio, refetch }: Props) => {
  const { checkIfHasRole } = useAuthContext();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConfirmDisableDialog, setShowConfirmDisableDialog] = useState(false);

  const isEnabled = portfolio.is_enabled;

  const canDisablePortfolio = checkIfHasRole('CAN_DISABLE_PORTFOLIO');
  const canViewCustomers = checkIfHasRole('CAN_VIEW_CUSTOMERS');
  const canViewCampaigns = checkIfHasRole('CAN_READ_CAMPAIGNS');
  const canEditPortfolio = checkIfHasRole('CAN_EDIT_PORTFOLIO');

  const showMenu = canDisablePortfolio || canViewCustomers || canViewCampaigns || canEditPortfolio;

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      px={4}
      py={3}
    >
      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Heading
            size={{ base: 'xs', md: 'md' }}
            textDecoration={isEnabled ? 'none' : 'line-through'}
            color={isEnabled ? 'fg.default' : 'fg.error'}
          >
            {portfolio.name}
          </Heading>
          {portfolio.max_age && (
            <Tag.Root size="md" bg="argos.fg" color="grayscale" fontWeight={600} textTransform="uppercase">
              <Tag.Label>
                Idade máxima:
                {' '}
                <b>
                  {portfolio.max_age}
                </b>
              </Tag.Label>
            </Tag.Root>
          )}
          <CreatedBy user={portfolio.user} createdDate={portfolio.created_date} />
        </Box>
        {isEnabled && showMenu && (
          <Flex alignItems="center">
            <Menu.Root>
              <Menu.Trigger asChild>
                <IconButton variant="ghost" size="sm">
                  <LuEllipsisVertical />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {canViewCustomers && (
                      <Menu.Item
                        value="customers"
                        onClick={() => window.open(`/painel/clientes?portfolioId=${portfolio.id}`, '_blank')}
                      >
                        Ver clientes <LuExternalLink />
                      </Menu.Item>
                    )}
                    {canViewCampaigns && (
                      <Menu.Item
                        value="campaigns"
                        onClick={() => window.open(`/painel/campanhas?portfolioId=${portfolio.id}`, '_blank')}
                      >
                        Ver campanhas <LuExternalLink />
                      </Menu.Item>
                    )}
                    {canEditPortfolio && (
                      <Menu.Item value="edit" onClick={() => setShowEditDialog(true)}>
                        Editar
                      </Menu.Item>
                    )}
                    {canDisablePortfolio && (
                      <Menu.Item
                        value="delete"
                        color="fg.error"
                        _hover={{ bg: "bg.error", color: "fg.error" }}
                        onClick={() => setShowConfirmDisableDialog(true)}
                      >
                        Desabilitar
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>     
          </Flex>
        )}
      </Flex>

      {showEditDialog && (
        <UpsertDialog
          handleClose={() => setShowEditDialog(false)}
          afterSubmit={refetch}
          portfolio={portfolio}
        />
      )}
      {showConfirmDisableDialog && (
        <ConfirmDisableDialog
          portfolio={portfolio}
          handleClose={() => setShowConfirmDisableDialog(false)}
          afterSubmit={refetch}
        />
      )}
    </Card.Root>
  );
};

export default PortfolioCard;
